import { doc, getDoc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { invalidateEmployeeUsersCache } from './attendanceDb';
import { isHrBootstrapEmail } from '../config/hrBootstrap';

const SETTINGS_APP = () => doc(db, 'settings', 'app');

/** Emails added under Admin access (settings/app.hrAdminEmails map). */
export async function isHrAdminEmailInSettings(email) {
  if (!email || !db) return false;
  const key = String(email).trim().toLowerCase();
  try {
    const snap = await getDoc(SETTINGS_APP());
    if (!snap.exists()) return false;
    const m = snap.data().hrAdminEmails;
    if (!m || typeof m !== 'object') return false;
    return m[key] === true;
  } catch {
    return false;
  }
}

/** Static list/domain OR email added in Firestore Admin access. */
export async function shouldGrantHrBootstrap(email) {
  if (!email) return false;
  if (isHrBootstrapEmail(email)) return true;
  return await isHrAdminEmailInSettings(email);
}

/**
 * Ensures Firestore `users/{email}` has role HR for bootstrap emails, and removes stale pending row.
 * Requires matching Firestore rules for self-write (see firestore.rules).
 */
export async function ensureHrBootstrapProfile(user) {
  if (!user?.email || !db) return;
  const email = user.email;
  if (!(await shouldGrantHrBootstrap(email))) return;

  const userRef = doc(db, 'users', email);
  const pendingRef = doc(db, 'pending_users', email);
  const [uSnap, pSnap] = await Promise.all([getDoc(userRef), getDoc(pendingRef)]);
  const role = (uSnap.data()?.role || '').toUpperCase();
  const needsHr = !uSnap.exists() || role !== 'HR';

  if (!needsHr) {
    if (pSnap.exists()) {
      try {
        await deleteDoc(pendingRef);
      } catch {
        /* rules */
      }
    }
    return;
  }

  await setDoc(
    userRef,
    {
      email,
      name: uSnap.data()?.name || user.displayName || email.split('@')[0],
      role: 'HR',
      status: 'APPROVED',
      department: uSnap.data()?.department || 'HR',
      leaveBalance: uSnap.data()?.leaveBalance || { casual: 1 },
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
  if (pSnap.exists()) {
    try {
      await deleteDoc(pendingRef);
    } catch {
      /* */
    }
  }
  invalidateEmployeeUsersCache();
}

/** Real-time list of emails (keys where value === true). */
export function subscribeHrAdminEmails(callback) {
  if (!db) {
    callback([]);
    return () => {};
  }
  return onSnapshot(
    SETTINGS_APP(),
    (snap) => {
      const m = snap.exists() && snap.data().hrAdminEmails && typeof snap.data().hrAdminEmails === 'object'
        ? snap.data().hrAdminEmails
        : {};
      const list = Object.keys(m)
        .filter((k) => m[k] === true)
        .sort((a, b) => a.localeCompare(b));
      callback(list);
    },
    () => callback([])
  );
}

export async function addHrAdminEmail(email) {
  if (!db) throw new Error('Firestore not available');
  const key = String(email).trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(key)) {
    throw new Error('Enter a valid email address');
  }
  const ref = SETTINGS_APP();
  const snap = await getDoc(ref);
  const prev =
    snap.exists() && snap.data().hrAdminEmails && typeof snap.data().hrAdminEmails === 'object'
      ? { ...snap.data().hrAdminEmails }
      : {};
  prev[key] = true;
  await setDoc(ref, { hrAdminEmails: prev, updatedAt: new Date().toISOString() }, { merge: true });
}

export async function removeHrAdminEmail(email) {
  if (!db) throw new Error('Firestore not available');
  const key = String(email).trim().toLowerCase();
  const ref = SETTINGS_APP();
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const prev =
    snap.data().hrAdminEmails && typeof snap.data().hrAdminEmails === 'object'
      ? { ...snap.data().hrAdminEmails }
      : {};
  delete prev[key];
  await setDoc(ref, { hrAdminEmails: prev, updatedAt: new Date().toISOString() }, { merge: true });
}
